import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as querySourcesApi from './query-sources.api';

import type {
  CreateQuerySourceRequest,
  QuerySourceListQuery,
  QuerySourceOptionsQuery,
  UpdateQuerySourceRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: QuerySourceListQuery) => ['query-sources', 'list', query] as const,
  detail: (id: string) => ['query-sources', 'detail', id] as const,
  options: (query?: QuerySourceOptionsQuery) => ['query-sources', 'options', query] as const,
};

export const useQuerySourcesList = (query: QuerySourceListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => querySourcesApi.listQuerySources(query),
    staleTime: 30_000,
  });

export const useQuerySourceOptions = (query?: QuerySourceOptionsQuery) =>
  useQuery({
    queryKey: QK.options(query),
    queryFn: () => querySourcesApi.getQuerySourceOptions(query),
    staleTime: 60_000,
  });

export const useCreateQuerySource = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQuerySourceRequest) => querySourcesApi.createQuerySource(payload),
    onSuccess: (qs) => {
      qc.invalidateQueries({ queryKey: ['query-sources', 'list'] });
      qc.invalidateQueries({ queryKey: ['query-sources', 'options'] });
      toast.success(`Added ${qs.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add query source'),
  });
};

export const useUpdateQuerySource = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateQuerySourceRequest }) =>
      querySourcesApi.updateQuerySource(id, payload),
    onSuccess: (qs) => {
      qc.invalidateQueries({ queryKey: ['query-sources', 'list'] });
      qc.invalidateQueries({ queryKey: ['query-sources', 'options'] });
      qc.invalidateQueries({ queryKey: QK.detail(qs.id) });
      toast.success('Query source updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update query source'),
  });
};

export const useDeleteQuerySource = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => querySourcesApi.deleteQuerySource(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['query-sources', 'list'] });
      qc.invalidateQueries({ queryKey: ['query-sources', 'options'] });
      toast.success('Query source deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete query source'),
  });
};

export const useBulkDeleteQuerySources = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => querySourcesApi.bulkDeleteQuerySources(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['query-sources', 'list'] });
      qc.invalidateQueries({ queryKey: ['query-sources', 'options'] });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete query sources'),
  });
};

export const useBulkSetQuerySourcesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      querySourcesApi.bulkSetQuerySourcesStatus(ids, isActive),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['query-sources', 'list'] });
      qc.invalidateQueries({ queryKey: ['query-sources', 'options'] });
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update query source status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
